<template>
  <Modal id="activity-finder-bookmarked-items" v-model="visible" flyout>
    <template v-slot:modal-title>
      <font-awesome-icon icon="bookmark" />
      <span class="bookmarked-items">{{ 'Bookmarked items' | t }}</span>
    </template>
    <template>
      <div class="bookmarked-items-modal-content">
        <div v-if="!cartItems.length">
          <BookmarkFeatureDescription />
        </div>
        <div v-else>
          <div class="message">
            <p>
              <strong>{{ 'The following items are NOT reserved.' | t }}</strong>
              {{ 'Others may still register for them.' | t }}
            </p>
          </div>

          <div class="cart-items">
            <div v-for="(item, index) in cartItems" :key="item.item.nid + '-' + index" class="item">
              <div class="title">
                <font-awesome-icon icon="bookmark" />
                {{ item.item.name }}
              </div>

              <div class="session-info">
                <span class="age">
                  <span class="age-label">Age:</span>
                  <AgeIcon v-if="item.age" :age="parseInt(item.age)" :ages="ages" big/>
                  <span v-else class="info">
                    {{ item.item.ages }}
                  </span>
                </span>
                <span class="spots">
                  <AvailableSpots
                    v-if="!disableSpotsAvailable && item.item.spots_available !== ''"
                    :spots="Number(item.item.spots_available)"
                    :wait-list="Number(item.item.wait_list_availability)"
                  />
                </span>
                <div class="item-detail dates">
                  <Icon icon="material-symbols:calendar-today-outline" />
                  <span>
                    <span class="info">{{ item.item.dates }}</span>
                    <br />
                    <span class="details">{{ item.item.days }}</span>
                  </span>
                </div>
                <div class="item-detail schedule">
                  <Icon icon="material-symbols:schedule-outline"/>
                  <span class="schedule-items">
                    <span
                      v-for="(schedule, schedule_index) in item.item.schedule"
                      :key="schedule_index"
                      class="schedule-item"
                    >
                      <span class="info">{{ schedule.time }}</span>
                      <br />
                      <span class="details">{{ schedule.days }}</span>
                    </span>
                  </span>
                </div>
              </div>
              <div class="actions">
                <template v-if="buttonsState[index] === 'default'">
                  <a
                    key="remove"
                    role="button"
                    class="remove"
                    title="Remove"
                    @click="removeItem(index)"
                  >
                    <i class="fa fa-trash"></i>
                  </a>
                  <a
                    key="register"
                    role="button"
                    class="btn btn-lg register"
                    :class="{ disabled: isRegisterDisabled(index), 'registration-not-started' : isRegistrationStarted(index) }"
                    :href="getRegistrationLink(item.item)"
                    target="_blank"
                    @click="register(index)"
                  >
                    {{ getButtonTitle(item.item) }}
                    <Icon icon="material-symbols:arrow-outward" />
                  </a>
                </template>
                <template v-else-if="buttonsState[index] === 'sentToRegister'">
                  <a
                    key="remove"
                    role="button"
                    class="remove"
                    title="Remove"
                    @click="removeItem(index)"
                  >
                    <i class="fa fa-trash"></i>
                  </a>
                  <a
                    key="reset"
                    role="button"
                    class="btn btn-lg action-taken"
                    @click="resetAction(index)"
                  >
                    <span>{{ 'Sent to register' | t }}</span>
                    <i class="fa fa-redo fa-repeat"></i>
                  </a>
                </template>
              </div>
            </div>

            <div class="bookmarked-items-footer">
              <div class="footer-content">
                <div class="left">
                  {{ 'Bookmarks are automatically cleared after 5 days.' | t }}
                </div>
                <div class="right">
                  <a role="button" class="clear-all" @click="removeItems">
                    {{ 'Clear All' | t }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BookmarkIcon :counter="cartItems.length" @click="hideModal()" />
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/modals/Modal.vue'
import BookmarkFeatureDescription from '@/components/modals/BookmarkFeatureDescription.vue'
import AgeIcon from '@/components/AgeIcon.vue'
import AvailableSpots from '@/components/AvailableSpots'
import BookmarkIcon from '@/components/BookmarkIcon'
import { Icon } from '@iconify/vue2'

export default {
  name: 'BookmarkedItemsModal',
  components: {
    Modal,
    BookmarkFeatureDescription,
    AgeIcon,
    AvailableSpots,
    BookmarkIcon,
    Icon
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    cartItems: {
      type: Array,
      required: true
    },
    ages: {
      type: Array,
      required: true
    },
    disableSpotsAvailable: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      visible: this.value,
      buttonsState: {}
    }
  },
  watch: {
    value() {
      this.visible = this.value
    },
    visible() {
      this.$emit('input', this.visible)
      if (this.visible) {
        this.buttonsState = {}
        this.cartItems.forEach((item, index) => {
          this.buttonsState = {
            ...this.buttonsState,
            ...{ [index]: 'default' }
          }
        })
      }
    },
    cartItems() {
      this.buttonsState = {}
      this.cartItems.forEach((item, index) => {
        this.buttonsState = {
          ...this.buttonsState,
          ...{ [index]: 'default' }
        }
      })
    }
  },
  methods: {
    register(index) {
      this.buttonsState = {
        ...this.buttonsState,
        ...{ [index]: 'sentToRegister' }
      }
      this.trackEvent(
        'register',
        'Click in bookmarked items',
        this.cartItems[index].item.product_id
      )
    },
    removeItem(index) {
      this.trackEvent(
        'unbookmark',
        'Click in bookmarked items',
        this.cartItems[index].item.product_id
      )
      this.$emit('removeItem', index)
    },
    removeItems() {
      this.trackEvent('unbookmark', 'Click in bookmarked items')
      this.$emit('removeItems')
    },
    resetAction(index) {
      this.buttonsState = {
        ...this.buttonsState,
        ...{ [index]: 'default' }
      }
    },
    getButtonTitle(index) {
      let title = this.t('Register')
      // parseInt('') -> NaN
      // parseInt('0') -> 0
      if (index.activity_type === 'personal') {
        title = this.t('Book this session')
      } else if (index.activity_type === 'small group training') {
        title = this.t('Reserve a spot')
      } else if (index.availability_status === 'closed' || parseInt(index.spots_available) === 0) {
        title = this.t('Full')
      } else if (index.availability_status === 'open' && !index.link.includes('nolink') && index.registration_start > index.current_time) {
        let sub_title = 'Registration opens ' + index.registration_start_formatted
        title = this.t(sub_title)
      } else if (index.availability_status === 'open' && !index.link.includes('nolink')) {
        title = this.t('Sign Up')
      } else if (index.availability_status === 'open' && index.link.includes('nolink')) {
        title = this.t('Drop-In Program')
      }

      return title
    },
    getRegistrationLink(item) {
      let reg_link_item = item.link
      if (item.availability_status === 'closed' || parseInt(item.spots_available) === 0) {
        reg_link_item = ''
      } else if (item.availability_status === 'open' && item.link.includes('nolink')) {
        reg_link_item = ''
      }

      return reg_link_item
    },
    isRegisterDisabled(index) {
      // parseInt('') -> NaN
      // parseInt('0') -> 0
      if (this.cartItems[index].item.availability_status === 'closed' || parseInt(this.cartItems[index].item.spots_available === 0)) {
        return true
      } else if (this.cartItems[index].item.availability_status === 'open' && !this.cartItems[index].item.link.includes('nolink') && this.cartItems[index].item.registration_start > this.cartItems[index].item.current_time) {
        return true
      } else if (this.cartItems[index].item.availability_status === 'open' && this.cartItems[index].item.link.includes('nolink')) {
        return true
      }
      return (
        parseInt(this.cartItems[index].item.spots_available) === 0 &&
        !this.cartItems[index].item.wait_list_availability
      )
    },
    isRegistrationStarted(index) {
      let reg_started = false
      if (this.cartItems[index].item.availability_status === 'open' && !this.cartItems[index].item.link.includes('nolink') && this.cartItems[index].item.registration_start > this.cartItems[index].item.current_time) {
        reg_started = true
      }

      return reg_started
    },
    hideModal() {
      this.visible = false
    }
  }
}
</script>

<style lang="scss">
.af-modal-content {
  .bookmarked-items {
    font-size: 18px;
    font-weight: 700;
    line-height: 28px;
    text-transform: capitalize;
  }
}

.bookmarked-items-modal-content {
  padding: 20px 10px;
  color: $af-black;

  .row {
    margin-left: -5px !important;
    margin-right: -5px !important;
  }

  [class*='col-'] {
    padding-left: 5px;
    padding-right: 5px;
  }

  .message {
    padding: 0 10px;
    font-size: 18px;
    line-height: 28px;
    margin-bottom: 16px;
  }

  .cart-items {
    .item {
      padding: 10px;
      border: 1px solid $af-border-gray;
      border-radius: $af-border-radius;
      margin-bottom: 10px;
      position: relative;

      &:last-child {
        margin-bottom: 0;
      }

      .title {
        color: $af-blue;
        font-size: 18px;
        line-height: 28px;
        font-weight: bold;
        margin-bottom: 10px;
      }

      .item-detail {
        display: flex;
        margin-bottom: 10px;

        .schedule-items {
          display: flex;
          flex-direction: column;
        }

        .details {
          font-size: 14px;
          line-height: 18px;
        }

        svg {
          color: $af-black;
          margin-right: 10px;
          position: relative;
          top: 6px;
          height: 1.2rem;
          width: 1.2rem;
        }

        .fa,
        .svg-inline--fa {
          font-size: 20px;
          color: $af-dark-gray;
          margin-right: 10px;
          width: 20px;
          text-align: center;
          flex-shrink: 0;
        }
      }

      .info {
        font-size: 14px;
        line-height: 20px;
      }

      .age {
        display: flex;
        justify-content: start;
        margin-bottom: 10px;

        .age-label {
          font-size: 14px;
          line-height: 30px;
        }

        .info {
          line-height: 30px;
        }
      }

      .spots {
        display: flex;
        justify-content: start;
        margin-bottom: 10px;

        .available-spots-component {
          justify-content: start;
          margin: 0;
        }
      }

      .actions {
        display: flex;
        justify-content: space-between;
        margin-top: 15px;

        .register {
          background-color: $af-violet;
          color: $white;
          flex-grow: 1;
          border-radius: 5px;
          font-size: 18px;
          line-height: 50px;
          font-weight: bolder;
          padding: 0;

          svg {
            height: 24px;
            width: 24px;
          }
        }

        .remove {
          line-height: 46px;
          display: inline-block;
          width: 50px;
          height: 50px;
          border: 2px solid $af-blue;
          border-radius: 5px;
          text-align: center;
          margin-right: 10px;

          .fa,
          .svg-inline--fa {
            font-size: 18px;
            line-height: 46px;
          }
        }

        .action-taken {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background-color: $af-light-gray;
          color: $af-darker-gray;
          flex-grow: 1;
          border-radius: 5px;
          font-size: 18px;
          line-height: 50px;
          font-weight: bolder;
          padding: 0 15px 0 20px;

          .fa {
            font-size: 20px;
          }
        }
      }
    }
  }

  .bookmarked-items-footer {
    height: 126px;

    .footer-content {
      display: flex;
      justify-content: space-between;
      position: fixed;
      bottom: 0;
      margin-left: -10px;
      margin-right: -10px;
      padding: 10px 20px;
      background-color: $af-light-gray;
      width: calc(100% - 2px);
      max-width: 358px;
      font-size: 14px;
      line-height: 18px;

      .left {
        max-width: 220px;
      }

      .right {
        display: flex;
        flex-direction: column;
        justify-content: space-around;
      }

      .clear-all {
        color: $af-blue;
        font-weight: bold;
      }
    }
  }
}
</style>
